import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, ArrowLeft, Check, Compass } from "lucide-react";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { toast } from "sonner";
import BrandMark from "@/components/BrandMark";
import N8Emblem from "@/components/N8Emblem";

const STEPS = 3;

export default function Onboarding({ profile, meta, onDone }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const [goals, setGoals] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uCheck, setUCheck] = useState(null);   // { available, error, suggestions }
  const [uChecking, setUChecking] = useState(false);

  // Open only when profile is loaded and NOT onboarded
  useEffect(() => {
    if (!profile) return;
    if (!profile.onboarded) {
      setOpen(true);
      setName(profile.name && profile.name !== "Wanderer" ? profile.name : "");
      setUsername(profile.username || "");
      setAgeGroup(profile.age_group || "");
      setGoals(Array.isArray(profile.longevity_goals) ? profile.longevity_goals.slice(0, 3) : []);
    }
  }, [profile]);

  // Real-time username availability during step 0.
  useEffect(() => {
    const trimmed = (username || "").replace(/^@+/, "").trim();
    if (!trimmed) { setUCheck(null); return; }
    let cancelled = false;
    setUChecking(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await endpoints.checkUsername(trimmed);
        if (!cancelled) setUCheck(data);
      } catch { /* silent */ }
      finally { if (!cancelled) setUChecking(false); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [username]);

  const canNext = useMemo(() => {
    if (step === 0) {
      const nameOk = name.trim().length >= 1;
      const trimmed = (username || "").replace(/^@+/, "").trim();
      const usernameOk = trimmed.length >= 3 && uCheck && uCheck.available;
      return nameOk && usernameOk;
    }
    if (step === 1) return !!ageGroup;
    if (step === 2) return goals.length >= 1 && goals.length <= 3;
    return true;
  }, [step, name, username, uCheck, ageGroup, goals]);

  const next = () => setStep((s) => Math.min(STEPS - 1, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const toggleGoal = (slug) => {
    setGoals((prev) => {
      if (prev.includes(slug)) return prev.filter((g) => g !== slug);
      if (prev.length >= 3) {
        toast("Pick your top three. You can change them later.");
        return prev;
      }
      return [...prev, slug];
    });
  };

  const finish = async () => {
    setSaving(true);
    try {
      const trimmed = (username || "").replace(/^@+/, "").trim().toLowerCase();
      await endpoints.updateProfile({
        name: name.trim(),
        display_name: name.trim(),
        username: trimmed,
        age_group: ageGroup,
        longevity_goals: goals,
        onboarded: true,
      });
      setOpen(false);
      onDone && onDone();
      toast.success("Welcome in. Let's meet your Compass.");
      // Land the user directly on the Discover phase of Becoming so the
      // first real minute of the app is self-discovery, not another form.
      navigate("/becoming?section=discover", { replace: true });
    } catch { toast.error("Could not save."); }
    setSaving(false);
  };

  if (!profile || !meta) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { /* prevent close via backdrop until finished */ if (v) setOpen(true); }}>
      <DialogContent
        data-testid="onboarding-dialog"
        className="bg-[hsl(var(--paper))] border-[hsl(var(--border))] max-w-lg p-0 overflow-hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <VisuallyHidden.Root>
          <DialogTitle>Welcome onboarding</DialogTitle>
          <DialogDescription>Three quick steps to personalise your wins feed</DialogDescription>
        </VisuallyHidden.Root>
        {/* Progress dots */}
        <div className="px-8 pt-8 flex items-center gap-2" data-testid="onboarding-progress">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                i <= step ? "bg-[hsl(var(--clay))]" : "bg-[hsl(var(--sand))]"
              }`}
            />
          ))}
        </div>

        <div className="px-8 py-8">
          <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
            Step {step + 1} of {STEPS}
          </div>

          {step === 0 && (
            <div data-testid="step-name">
              <div className="flex justify-center mb-5" data-testid="onboarding-emblem-wrap">
                <N8Emblem
                  mode="full"
                  size={96}
                  ariaLabel="N8ONE emblem"
                  testid="onboarding-emblem"
                />
              </div>
              <div className="inline-flex items-center gap-2" data-testid="onboarding-brand">
                <BrandMark className="font-display text-2xl" testid="brand-wordmark-onboarding" />
              </div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))]">
                The Art Of Becoming
              </div>
              <h2 className="font-display text-4xl mt-4 tracking-tight leading-[1.1]">
                Welcome. What should we call you?
              </h2>
              <p className="mt-3 text-[hsl(var(--muted-foreground))]">
                A first name is enough. Under thirty seconds and you&apos;re in.
              </p>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && canNext) next(); }}
                placeholder="Your display name"
                data-testid="onboarding-name-input"
                autoFocus
                className="mt-6 bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--clay))] text-lg h-12"
              />

              <div className="mt-4">
                <label className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
                  Choose your username
                </label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]">@</span>
                  <Input
                    value={username.replace(/^@+/, "")}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="yourhandle"
                    autoComplete="off"
                    spellCheck={false}
                    data-testid="onboarding-username-input"
                    className="bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--clay))] text-base h-11 pl-7"
                  />
                </div>
                <div className="mt-1 text-xs" data-testid="onboarding-username-status">
                  {uChecking && <span className="text-[hsl(var(--muted-foreground))]">Checking availability…</span>}
                  {!uChecking && uCheck?.available && (
                    <span className="text-[hsl(var(--moss))]">@{uCheck.username} is available.</span>
                  )}
                  {!uChecking && uCheck && !uCheck.available && (
                    <span className="text-[hsl(var(--terracotta))]">{uCheck.error || "That username is already taken."}</span>
                  )}
                  {!uChecking && !uCheck && (
                    <span className="text-[hsl(var(--muted-foreground))]">
                      Your unique identity within The Collective. Letters, numbers, underscore or period.
                    </span>
                  )}
                </div>
                {!uChecking && uCheck && !uCheck.available && Array.isArray(uCheck.suggestions) && uCheck.suggestions.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="onboarding-username-suggestions">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] mr-1">Try</span>
                    {uCheck.suggestions.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setUsername(s)}
                        data-testid={`onboarding-username-suggestion-${s}`}
                        className="px-2.5 py-1 rounded-full text-xs border hairline bg-[hsl(var(--paper))] hover:bg-[hsl(var(--sand))]/50 tab-link"
                      >
                        @{s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="step-age">
              <h2 className="font-display text-4xl mt-3 tracking-tight leading-[1.1]">
                Which decade are you in, {name.trim() || "friend"}?
              </h2>
              <p className="mt-3 text-[hsl(var(--muted-foreground))]">
                So we surface wins from people close to your season.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-3">
                {meta.age_groups.map((a) => {
                  const on = ageGroup === a;
                  return (
                    <button
                      key={a}
                      type="button"
                      onClick={() => setAgeGroup(a)}
                      data-testid={`onboarding-age-${a}`}
                      className={`px-4 py-4 rounded-md border hairline text-left tab-link ${
                        on
                          ? "bg-[hsl(var(--fg))] text-[hsl(var(--paper))] border-transparent"
                          : "hover:bg-[hsl(var(--sand))]"
                      }`}
                    >
                      <div className="font-display text-2xl">{a}</div>
                      <div className={`text-[11px] uppercase tracking-[0.18em] mt-1 ${on ? "opacity-70" : "text-[hsl(var(--muted-foreground))]"}`}>
                        {ageLabel(a)}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-goals">
              <h2 className="font-display text-4xl mt-3 tracking-tight leading-[1.1]">
                Pick your top three.
              </h2>
              <p className="mt-3 text-[hsl(var(--muted-foreground))]">
                A first sketch, not a contract. Next we&rsquo;ll open your Soul Compass — a quiet
                40-question reflection that reveals who you already are.
              </p>
              <div className="mt-6 flex flex-wrap gap-2" data-testid="onboarding-goal-list">
                {meta.goal_tags.map((g) => {
                  const on = goals.includes(g.slug);
                  return (
                    <button
                      key={g.slug}
                      type="button"
                      onClick={() => toggleGoal(g.slug)}
                      data-testid={`onboarding-goal-${g.slug}`}
                      className={`px-4 py-2 rounded-full text-sm border hairline tab-link inline-flex items-center gap-2 ${
                        on
                          ? "bg-[hsl(var(--moss))] text-[hsl(var(--paper))] border-transparent"
                          : "hover:bg-[hsl(var(--sand))]"
                      }`}
                    >
                      {on && <Check size={14} />}
                      {g.name}
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 text-xs text-[hsl(var(--muted-foreground))]" data-testid="onboarding-goal-count">
                {goals.length} of 3 selected
              </div>
            </div>
          )}
        </div>

        <div className="px-8 pb-8 pt-2 flex items-center justify-between border-t hairline bg-[hsl(var(--sand))]/40">
          <button
            onClick={back}
            disabled={step === 0}
            data-testid="onboarding-back"
            className="text-sm text-[hsl(var(--muted-foreground))] inline-flex items-center gap-1 disabled:opacity-0 hover:text-[hsl(var(--fg))] tab-link"
          >
            <ArrowLeft size={14} /> Back
          </button>

          {step < STEPS - 1 ? (
            <Button
              onClick={next}
              disabled={!canNext}
              data-testid="onboarding-next"
              className="rounded-full bg-[hsl(var(--clay))] hover:bg-[hsl(var(--clay))]/90 text-[hsl(var(--paper))]"
            >
              Continue <ArrowRight size={14} className="ml-2" />
            </Button>
          ) : (
            <Button
              onClick={finish}
              disabled={!canNext || saving}
              data-testid="onboarding-finish"
              className="rounded-full bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90 text-[hsl(var(--paper))]"
            >
              {saving ? "Saving…" : (<><Compass size={14} className="mr-2" /> Meet your Compass</>)}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ageLabel(a) {
  switch (a) {
    case "20-30": return "starting the arc";
    case "30-40": return "sharpening habits";
    case "40-50": return "coming into your own";
    case "50-60": return "layering wisdom";
    case "60+": return "the long view";
    default: return "";
  }
}
